import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { uploadImage } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useLanguage } from '../context/LanguageContext';

const formatDate = (dateString, language) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const locale = language === 'de' ? 'de-DE' : 'en-US';
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const ListDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { language } = useLanguage();

  const [list, setList] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddVocab, setShowAddVocab] = useState(false);
  const [newWord1, setNewWord1] = useState('');
  const [newWord2, setNewWord2] = useState('');
  const [newDescription1, setNewDescription1] = useState('');
  const [newDescription2, setNewDescription2] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newNote, setNewNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingVocab, setEditingVocab] = useState(null);
  const [editWord1, setEditWord1] = useState('');
  const [editWord2, setEditWord2] = useState('');
  const [editDescription1, setEditDescription1] = useState('');
  const [editDescription2, setEditDescription2] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');
  const [editNote, setEditNote] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [exportFormat, setExportFormat] = useState('simple'); // 'simple' (2 cols) or 'extended' (6 cols)
  const [addVocabErrors, setAddVocabErrors] = useState({});
  const [editVocabErrors, setEditVocabErrors] = useState({});
  const [isUploadingNewImage, setIsUploadingNewImage] = useState(false);
  const [isUploadingEditImage, setIsUploadingEditImage] = useState(false);
  const newImageInputRef = useRef(null);
  const editImageInputRef = useRef(null);
  // Feature #138: Box filter state
  const [boxFilter, setBoxFilter] = useState(null); // null = show all, 1-5 = filter by box

  useEffect(() => {
    fetchList();
  }, [id]);

  const fetchList = async () => {
    try {
      const response = await api.get(`/lists/${id}`);
      setList(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError(t('errors.notFound'));
      } else {
        setError(err.message || t('errors.serverError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/lists/${id}`);
      navigate('/lists');
    } catch (err) {
      setError(err.message || t('errors.serverError'));
    }
    setShowDeleteModal(false);
  };

  const handleAddVocabulary = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!newWord1.trim()) {
      errors.word1 = t('errors.required');
    }
    if (!newWord2.trim()) {
      errors.word2 = t('errors.required');
    }
    if (Object.keys(errors).length > 0) {
      setAddVocabErrors(errors);
      return;
    }
    setAddVocabErrors({});

    setIsSubmitting(true);
    try {
      await api.post(`/lists/${id}/vocabularies`, {
        word1: newWord1.trim(),
        word2: newWord2.trim(),
        description1: newDescription1.trim(),
        description2: newDescription2.trim(),
        imageUrl: newImageUrl.trim(),
        note: newNote.trim(),
      });
      setNewWord1('');
      setNewWord2('');
      setNewDescription1('');
      setNewDescription2('');
      setNewImageUrl('');
      setNewNote('');
      setShowAddVocab(false);
      showToast(t('common.success'), 'success');
      fetchList();
    } catch (err) {
      if (err.message && err.message.includes('200')) {
        setAddVocabErrors({ form: t('vocabulary.limitReached') });
      } else {
        setError(err.message || t('errors.serverError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteVocabulary = async (vocabId) => {
    if (!window.confirm(t('vocabulary.confirmDelete'))) return;

    try {
      await api.delete(`/vocabularies/${vocabId}`);
      fetchList();
    } catch (err) {
      setError(err.message || t('errors.serverError'));
    }
  };

  const handleStartEdit = (vocab) => {
    setEditingVocab(vocab.id);
    setEditWord1(vocab.word1);
    setEditWord2(vocab.word2);
    setEditDescription1(vocab.description1 || '');
    setEditDescription2(vocab.description2 || '');
    setEditImageUrl(vocab.imageUrl || '');
    setEditNote(vocab.note || '');
    setEditVocabErrors({});
  };

  const handleCancelEdit = () => {
    setEditingVocab(null);
    setEditWord1('');
    setEditWord2('');
    setEditDescription1('');
    setEditDescription2('');
    setEditImageUrl('');
    setEditNote('');
    setEditVocabErrors({});
  };

  const handleSaveEdit = async (vocabId) => {
    const errors = {};
    if (!editWord1.trim()) {
      errors.word1 = t('errors.required');
    }
    if (!editWord2.trim()) {
      errors.word2 = t('errors.required');
    }
    if (Object.keys(errors).length > 0) {
      setEditVocabErrors(errors);
      return;
    }
    setEditVocabErrors({});

    setIsSubmitting(true);
    try {
      await api.put(`/vocabularies/${vocabId}`, {
        word1: editWord1.trim(),
        word2: editWord2.trim(),
        description1: editDescription1.trim(),
        description2: editDescription2.trim(),
        imageUrl: editImageUrl.trim(),
        note: editNote.trim(),
      });
      setEditingVocab(null);
      setEditWord1('');
      setEditWord2('');
      setEditDescription1('');
      setEditDescription2('');
      setEditImageUrl('');
      setEditNote('');
      showToast(t('common.success'), 'success');
      fetchList();
    } catch (err) {
      setError(err.message || t('errors.serverError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShare = async () => {
    try {
      const response = await api.get(`/lists/${id}/share`);
      setShareData(response.data);
      setShowShareModal(true);
    } catch (err) {
      setError(err.message || t('errors.serverError'));
    }
  };

  // Handle image upload for new vocabulary
  const handleNewImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingNewImage(true);
    try {
      const result = await uploadImage(file);
      if (result.imageUrl) {
        setNewImageUrl(result.imageUrl);
        showToast(t('csv.uploadSuccess'), 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.message || t('csv.uploadError'), 'error');
    } finally {
      setIsUploadingNewImage(false);
      // Reset the input so the same file can be selected again
      if (newImageInputRef.current) {
        newImageInputRef.current.value = '';
      }
    }
  };

  // Handle image upload for editing vocabulary
  const handleEditImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingEditImage(true);
    try {
      const result = await uploadImage(file);
      if (result.imageUrl) {
        setEditImageUrl(result.imageUrl);
        showToast(t('csv.uploadSuccess'), 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.message || t('csv.uploadError'), 'error');
    } finally {
      setIsUploadingEditImage(false);
      // Reset the input so the same file can be selected again
      if (editImageInputRef.current) {
        editImageInputRef.current.value = '';
      }
    }
  };

  if (isLoading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error && !list) {
    return (
      <div className="empty-state">
        <h2 className="empty-state-title">{error}</h2>
        <button className="btn btn-primary" onClick={() => navigate('/lists')}>
          {t('common.back')}
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1rem' }}>
        <button
          className="btn btn-outline"
          onClick={() => navigate('/lists')}
          style={{ marginBottom: '1rem' }}
        >
          &larr; {t('common.back')}
        </button>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
          <div>
            <h1 className="page-title" style={{ marginBottom: '0.25rem' }}>
              {list.name}
            </h1>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              {list.language1} / {list.language2}
            </p>
            {list.createdAt && (
              <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                {t('lists.created')}: {formatDate(list.createdAt, language)}
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {!list.isQrImported && (
              <button
                className="btn btn-outline btn-icon"
                onClick={() => navigate(`/lists/${id}/edit`)}
                aria-label={t('lists.edit')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
            )}
            <button
              className="btn btn-error btn-icon"
              onClick={() => setShowDeleteModal(true)}
              aria-label={t('lists.delete')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button
          className="btn btn-primary"
          onClick={() => navigate(`/training/${id}`)}
          disabled={!list.vocabularies?.length}
        >
          {t('training.start')}
        </button>
        <button
          className="btn btn-outline"
          onClick={() => setShowAddVocab(true)}
        >
          {t('vocabulary.add')}
        </button>
        <button
          className="btn btn-outline"
          onClick={handleShare}
        >
          {t('lists.share')}
        </button>
      </div>

      {/* Add vocabulary form */}
      {showAddVocab && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>{t('vocabulary.add')}</h3>
          {addVocabErrors.form && (
            <div className="form-error" style={{ marginBottom: '1rem' }}>
              {addVocabErrors.form}
            </div>
          )}
          <form onSubmit={handleAddVocabulary}>
            <div className="form-group">
              <label className="form-label">{list.language1}</label>
              <input
                type="text"
                className={`form-input ${addVocabErrors.word1 ? 'error' : ''}`}
                value={newWord1}
                onChange={(e) => setNewWord1(e.target.value)}
                autoFocus
              />
              {addVocabErrors.word1 && <div className="form-error">{addVocabErrors.word1}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">{list.language2}</label>
              <input
                type="text"
                className={`form-input ${addVocabErrors.word2 ? 'error' : ''}`}
                value={newWord2}
                onChange={(e) => setNewWord2(e.target.value)}
              />
              {addVocabErrors.word2 && <div className="form-error">{addVocabErrors.word2}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">{t('csv.description1')} ({t('common.optional') || 'optional'})</label>
              <input
                type="text"
                className="form-input"
                value={newDescription1}
                onChange={(e) => setNewDescription1(e.target.value)}
                placeholder={t('csv.description1')}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('csv.description2')} ({t('common.optional') || 'optional'})</label>
              <input
                type="text"
                className="form-input"
                value={newDescription2}
                onChange={(e) => setNewDescription2(e.target.value)}
                placeholder={t('csv.description2')}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('csv.imageUrl')} ({t('common.optional') || 'optional'})</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <input
                  type="text"
                  className="form-input"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  placeholder="https://..."
                  style={{ flex: 1 }}
                />
                <input
                  type="file"
                  ref={newImageInputRef}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleNewImageUpload}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => newImageInputRef.current?.click()}
                  disabled={isUploadingNewImage}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {isUploadingNewImage ? (
                    <>
                      <span className="spinner-small" style={{ marginRight: '0.25rem' }}></span>
                      {t('csv.uploading')}
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.25rem' }}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                      {t('csv.uploadImage')}
                    </>
                  )}
                </button>
              </div>
              <small style={{ color: 'var(--color-text-secondary)', marginTop: '0.25rem', display: 'block' }}>
                {t('csv.maxFileSize')}
              </small>
            </div>
            <div className="form-group">
              <label className="form-label">{t('csv.note')} ({t('common.optional') || 'optional'})</label>
              <input
                type="text"
                className="form-input"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder={t('csv.note')}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {t('common.save')}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { setShowAddVocab(false); setAddVocabErrors({}); setNewDescription1(''); setNewDescription2(''); setNewImageUrl(''); setNewNote(''); }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Feature #138, #141 & #144: Box filter buttons - always visible when there are vocabularies */}
      {/* Feature #144: Colored buttons (red to green) and K1-K5 (German) / B1-B5 (English) labels */}
      {list.vocabularies?.length > 0 && (() => {
        // Calculate which boxes have at least one item
        const boxCounts = {};
        list.vocabularies.forEach(v => {
          boxCounts[v.box] = (boxCounts[v.box] || 0) + 1;
        });
        const availableBoxes = [1, 2, 3, 4, 5].filter(box => boxCounts[box] > 0);

        // Feature #144 & #146: Box colors from red (box 1) to green (box 5)
        // Feature #146: Buttons always have light colored backgrounds
        const boxColors = {
          1: { bg: '#FEE2E2', bgSelected: '#EF4444', border: '#EF4444', text: '#991B1B' }, // Light red -> Red
          2: { bg: '#FFEDD5', bgSelected: '#F97316', border: '#F97316', text: '#9A3412' }, // Light orange -> Orange
          3: { bg: '#FEF9C3', bgSelected: '#EAB308', border: '#EAB308', text: '#854D0E' }, // Light yellow -> Yellow
          4: { bg: '#ECFCCB', bgSelected: '#84CC16', border: '#84CC16', text: '#3F6212' }, // Light yellow-green -> Yellow-green
          5: { bg: '#DCFCE7', bgSelected: '#22C55E', border: '#22C55E', text: '#166534' }  // Light green -> Green
        };

        // Get box prefix from translations (K for German, B for English)
        const boxPrefix = t('vocabulary.boxPrefix') || 'B';

        // Feature #141: Always show the filter, even if all items are in the same box
        return (
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <button
              className={`btn ${boxFilter === null ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setBoxFilter(null)}
            >
              {t('common.all') || 'All'} ({list.vocabularies.length})
            </button>
            {availableBoxes.map(box => (
              <button
                key={box}
                className="btn"
                onClick={() => setBoxFilter(boxFilter === box ? null : box)}
                style={{
                  backgroundColor: boxFilter === box ? boxColors[box].bgSelected : boxColors[box].bg,
                  borderColor: boxColors[box].border,
                  color: boxFilter === box ? 'white' : boxColors[box].text,
                  fontWeight: boxFilter === box ? 'bold' : 'normal'
                }}
              >
                {boxPrefix}{box} ({boxCounts[box]})
              </button>
            ))}
          </div>
        );
      })()}

      {/* Vocabularies list */}
      <h2 style={{ marginBottom: '1rem' }}>
        {t('lists.vocabularies')} ({boxFilter !== null
          ? list.vocabularies?.filter(v => v.box === boxFilter).length || 0
          : list.vocabularies?.length || 0})
      </h2>

      {list.vocabularies?.length === 0 ? (
        <div className="empty-state">
          <p>{t('training.noVocabularies')}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {list.vocabularies
            ?.filter(vocab => boxFilter === null || vocab.box === boxFilter)
            .map((vocab) => (
            <div key={vocab.id} className="list-item">
              {editingVocab === vocab.id ? (
                <div style={{ width: '100%', padding: '0.5rem' }}>
                  <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                    <label className="form-label">{list.language1}</label>
                    <input
                      type="text"
                      className={`form-input ${editVocabErrors.word1 ? 'error' : ''}`}
                      value={editWord1}
                      onChange={(e) => setEditWord1(e.target.value)}
                      autoFocus
                    />
                    {editVocabErrors.word1 && <div className="form-error">{editVocabErrors.word1}</div>}
                  </div>
                  <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                    <label className="form-label">{list.language2}</label>
                    <input
                      type="text"
                      className={`form-input ${editVocabErrors.word2 ? 'error' : ''}`}
                      value={editWord2}
                      onChange={(e) => setEditWord2(e.target.value)}
                    />
                    {editVocabErrors.word2 && <div className="form-error">{editVocabErrors.word2}</div>}
                  </div>
                  <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                    <label className="form-label">{t('csv.description1')} ({t('common.optional') || 'optional'})</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editDescription1}
                      onChange={(e) => setEditDescription1(e.target.value)}
                      placeholder={t('csv.description1')}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                    <label className="form-label">{t('csv.description2')} ({t('common.optional') || 'optional'})</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editDescription2}
                      onChange={(e) => setEditDescription2(e.target.value)}
                      placeholder={t('csv.description2')}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                    <label className="form-label">{t('csv.imageUrl')} ({t('common.optional') || 'optional'})</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                      <input
                        type="text"
                        className="form-input"
                        value={editImageUrl}
                        onChange={(e) => setEditImageUrl(e.target.value)}
                        placeholder="https://..."
                        style={{ flex: 1 }}
                      />
                      <input
                        type="file"
                        ref={editImageInputRef}
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleEditImageUpload}
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => editImageInputRef.current?.click()}
                        disabled={isUploadingEditImage}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {isUploadingEditImage ? (
                          <>
                            <span className="spinner-small" style={{ marginRight: '0.25rem' }}></span>
                            {t('csv.uploading')}
                          </>
                        ) : (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.25rem' }}>
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="17 8 12 3 7 8"></polyline>
                              <line x1="12" y1="3" x2="12" y2="15"></line>
                            </svg>
                            {t('csv.uploadImage')}
                          </>
                        )}
                      </button>
                    </div>
                    <small style={{ color: 'var(--color-text-secondary)', marginTop: '0.25rem', display: 'block' }}>
                      {t('csv.maxFileSize')}
                    </small>
                  </div>
                  <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                    <label className="form-label">{t('csv.note')} ({t('common.optional') || 'optional'})</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editNote}
                      onChange={(e) => setEditNote(e.target.value)}
                      placeholder={t('csv.note')}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSaveEdit(vocab.id)}
                      disabled={isSubmitting}
                    >
                      {t('common.save')}
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={handleCancelEdit}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="list-item-content">
                    <div className="list-item-title">
                      {vocab.word1} &mdash; {vocab.word2}
                    </div>
                    <div className="list-item-subtitle">
                      {t('vocabulary.box')} {vocab.box}
                    </div>
                    {(vocab.description1 || vocab.description2 || vocab.imageUrl || vocab.note) && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                        {vocab.description1 && <div><strong>{t('csv.description1')}:</strong> {vocab.description1}</div>}
                        {vocab.description2 && <div><strong>{t('csv.description2')}:</strong> {vocab.description2}</div>}
                        {vocab.imageUrl && (
                          <div>
                            <strong>{t('csv.imageUrl')}:</strong>{' '}
                            <a href={vocab.imageUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                              {vocab.imageUrl.length > 30 ? vocab.imageUrl.slice(0, 30) + '...' : vocab.imageUrl}
                            </a>
                          </div>
                        )}
                        {vocab.note && <div><strong>{t('csv.note')}:</strong> {vocab.note}</div>}
                      </div>
                    )}
                  </div>
                  <div className="list-item-actions">
                    <button
                      className="btn btn-outline btn-icon"
                      onClick={() => handleStartEdit(vocab)}
                      aria-label={t('vocabulary.edit')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button
                      className="btn btn-outline btn-icon"
                      onClick={() => handleDeleteVocabulary(vocab.id)}
                      aria-label={t('vocabulary.delete')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">{t('lists.confirmDelete')}</h2>
            <p>{t('lists.deleteWarning')}</p>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowDeleteModal(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-error" onClick={handleDelete}>
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share modal */}
      {showShareModal && shareData && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">{t('lists.share')}</h2>
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(shareData.shareUrl + (exportFormat === 'extended' ? '?format=extended' : ''))}`}
                alt="QR Code"
                style={{ maxWidth: '200px', marginBottom: '1rem' }}
              />
              <div className="form-group">
                <label className="form-label">{t('lists.shareUrl')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={shareData.shareUrl + (exportFormat === 'extended' ? '?format=extended' : '')}
                  readOnly
                  onClick={(e) => e.target.select()}
                />
              </div>
            </div>

            {/* Export format selection */}
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label">{t('csv.exportFormat') || 'Export Format'}</label>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="exportFormat"
                    value="simple"
                    checked={exportFormat === 'simple'}
                    onChange={(e) => setExportFormat(e.target.value)}
                  />
                  {t('csv.simpleFormat') || '2 Columns (Simple)'}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="exportFormat"
                    value="extended"
                    checked={exportFormat === 'extended'}
                    onChange={(e) => setExportFormat(e.target.value)}
                  />
                  {t('csv.extendedFormat') || '6 Columns (Extended)'}
                </label>
              </div>
            </div>

            <div className="modal-actions" style={{ flexWrap: 'wrap' }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  const downloadUrl = `${shareData.shareUrl}?format=${exportFormat}`;
                  window.open(downloadUrl, '_blank');
                }}
              >
                {t('csv.downloadCsv') || 'Download CSV'}
              </button>
              <button
                className="btn btn-outline"
                onClick={() => {
                  navigator.clipboard.writeText(shareData.shareUrl + (exportFormat === 'extended' ? '?format=extended' : ''));
                }}
              >
                {t('common.copy')}
              </button>
              <button className="btn btn-outline" onClick={() => setShowShareModal(false)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListDetailPage;
